import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutGrid,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  XCircle,
  RefreshCw,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

const STATUS_CONFIG = {
  NOT_CONFIGURED: {
    label: "Not Configured",
    color: "text-muted-foreground",
    bg: "bg-muted/50",
    border: "border-border",
    dot: "bg-muted-foreground",
    icon: Circle,
  },
  CONFIGURING: {
    label: "Configuring",
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800",
    dot: "bg-amber-500",
    icon: Clock,
  },
  ACTIVE: {
    label: "Active",
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-950/30",
    border: "border-emerald-200 dark:border-emerald-800",
    dot: "bg-emerald-500",
    icon: CheckCircle2,
  },
  INACTIVE: {
    label: "Inactive",
    color: "text-red-500 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-800",
    dot: "bg-red-500",
    icon: XCircle,
  },
};

const CATEGORY_COLORS = {
  ACADEMIC: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  OPERATIONAL: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  FINANCIAL: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  GOVERNANCE: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800",
  INFRASTRUCTURE: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CONFIGURED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function CategoryBadge({ category }) {
  const cls = CATEGORY_COLORS[category] || CATEGORY_COLORS.ACADEMIC;
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
      {category}
    </span>
  );
}

function DomainCard({ domain, onClick }) {
  const cfg = STATUS_CONFIG[domain.status] || STATUS_CONFIG.NOT_CONFIGURED;
  const Icon = cfg.icon;
  return (
    <button
      data-testid={`setup-domain-card-${domain.domain_code}`}
      onClick={() => onClick(domain)}
      className={`
        group w-full text-left rounded-xl border p-4 transition-all duration-150
        hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5
        ${cfg.border} ${cfg.bg}
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CategoryBadge category={domain.domain_category} />
          </div>
          <h3 className="font-semibold text-foreground text-sm leading-snug mb-1 group-hover:text-primary transition-colors">
            {domain.domain_name}
          </h3>
          {domain.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {domain.description}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Icon className={`h-4 w-4 ${cfg.color}`} />
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-current/10">
        <StatusBadge status={domain.status} />
      </div>
    </button>
  );
}

function SummaryStats({ domains }) {
  const counts = {
    total: domains.length,
    active: domains.filter((d) => d.status === "ACTIVE").length,
    configuring: domains.filter((d) => d.status === "CONFIGURING").length,
    not_configured: domains.filter((d) => d.status === "NOT_CONFIGURED").length,
    inactive: domains.filter((d) => d.status === "INACTIVE").length,
  };
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
      data-testid="setup-summary-stats"
    >
      {[
        { label: "Total Domains", value: counts.total, cls: "text-foreground" },
        { label: "Active", value: counts.active, cls: "text-emerald-600 dark:text-emerald-400" },
        { label: "Configuring", value: counts.configuring, cls: "text-amber-600 dark:text-amber-400" },
        { label: "Not Configured", value: counts.not_configured, cls: "text-muted-foreground" },
      ].map((s) => (
        <div
          key={s.label}
          className="rounded-lg border border-border bg-card p-3 text-center"
        >
          <div className={`text-2xl font-bold ${s.cls}`}>{s.value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function SetupStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [institutionId, setInstitutionId] = useState(null);

  const fetchDomains = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/setup/domains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail?.message || err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setDomains(data.domains || []);
      setInstitutionId(data.institution_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleDomainClick = (domain) => {
    navigate(`/setup/domains/${domain.id}`, { state: { domain } });
  };

  // Group by category
  const byCategory = {};
  domains.forEach((d) => {
    const cat = d.domain_category || "ACADEMIC";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(d);
  });
  const CATEGORY_ORDER = ["ACADEMIC", "FINANCIAL", "OPERATIONAL", "GOVERNANCE", "INFRASTRUCTURE"];

  return (
    <div className="min-h-screen bg-background" data-testid="setup-studio-page">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
              >
                <LayoutGrid className="h-5 w-5" />
              </div>
              <div>
                <h1
                  className="text-xl font-bold text-foreground"
                  data-testid="setup-studio-title"
                >
                  Setup Studio
                </h1>
                <p className="text-xs text-muted-foreground">
                  Institutional Configuration Control Plane
                </p>
              </div>
            </div>
            <button
              data-testid="setup-refresh-btn"
              onClick={fetchDomains}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border hover:border-foreground/30"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="setup-modules-btn"
              onClick={() => navigate("/setup/module-registry")}
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border hover:border-foreground/30 bg-card"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Modules
            </button>
            <button
              data-testid="setup-readiness-btn"
              onClick={() => navigate("/setup/gates")}
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border hover:border-foreground/30 bg-card"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Readiness
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Institution context */}
        <div
          className="flex items-center gap-2 mb-6 text-sm text-muted-foreground"
          data-testid="setup-institution-context"
        >
          <span className="font-medium text-foreground">{user?.institution_name || "Institution"}</span>
          <span className="text-muted-foreground/50">·</span>
          <span className="font-mono text-xs opacity-60">{institutionId?.slice(0, 8)}…</span>
        </div>

        {/* Error state */}
        {error && (
          <div
            className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 mb-6"
            data-testid="setup-error-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">
                Failed to load setup domains
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{error}</p>
            </div>
            <button
              onClick={fetchDomains}
              className="ml-auto text-xs text-red-600 underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="setup-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-32 rounded-xl border border-border bg-muted/30 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Loaded: empty state */}
        {!loading && !error && domains.length === 0 && (
          <div
            className="flex flex-col items-center justify-center py-20 text-center"
            data-testid="setup-empty-state"
          >
            <LayoutGrid className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm font-medium text-muted-foreground">No domains configured</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Setup Studio domains will appear here once configured for this institution.
            </p>
          </div>
        )}

        {/* Loaded: domain cards */}
        {!loading && !error && domains.length > 0 && (
          <>
            <SummaryStats domains={domains} />

            {CATEGORY_ORDER.filter((cat) => byCategory[cat]).map((cat) => (
              <div key={cat} className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {cat.charAt(0) + cat.slice(1).toLowerCase()}
                  </h2>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">
                    {byCategory[cat].length}
                  </span>
                </div>
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  data-testid={`setup-category-grid-${cat}`}
                >
                  {byCategory[cat].map((domain) => (
                    <DomainCard
                      key={domain.id}
                      domain={domain}
                      onClick={handleDomainClick}
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
